import { Link } from "react-router-dom";
import FaqSchema from "@/components/FaqSchema";
import ResourceLayout, { AnswerBlock, DataTable } from "@/components/ResourceLayout";

const FAQS = [
  {
    q: "What is a good labour cost percentage in a restaurant?",
    a: "A healthy labour cost sits between 28 and 34 percent of net revenue for full service restaurants, 22 to 28 percent for quick service, and 32 to 38 percent for fine dining where technique demands more hands on the pass. Anything above 38 percent for two consecutive weeks needs a rota audit.",
  },
  {
    q: "How do you calculate labour cost per plate?",
    a: "Add every prep and service minute a plate consumes, multiply each block by the station's hourly rate divided by sixty, then add on-costs for super, payroll tax, and insurance. That total is the plate's labour cost. Combined with food cost it gives you the true cost to plate.",
  },
  {
    q: "What is the ideal labour to sales ratio during a service?",
    a: "Target one hour of paid labour per one hundred and twenty dollars of net revenue on a busy fine dining service. Casual full service targets one hundred and eighty to two hundred dollars per labour hour. Push the ratio too high and quality drops; push it too low and margin evaporates.",
  },
];

export default function LabourCost() {
  return (
    <ResourceLayout
      activeSlug="labour-cost"
      title="Labour Cost: how to price the hours behind every plate"
      lede="Standard formulas for computing labour per shift, per plate, and per hour of revenue. Includes typical labour cost percentages by service style and the on-cost loadings most operators forget to add on top of the base wage."
    >
      <FaqSchema
        id="labour-cost"
        title="Labour Cost Formulas and Targets for Restaurants | Culinaire Specialist"
        description="How to calculate labour cost per plate, per shift, and per hour of revenue. Includes fully loaded wage rates, target percentages by service style, and rota rules."
        canonicalUrl="https://www.culinairespecialist.com/resources/labour-cost"
        faqs={FAQS}
      />

      <AnswerBlock
        id="what-is-labour-cost"
        heading="What is labour cost in a hospitality kitchen?"
        answer="Labour cost is the total money a restaurant pays its team to produce a plate or run a shift, including base wages, penalty rates, superannuation, payroll tax, and workers' compensation insurance. The base hourly rate alone typically represents only 75 to 80 percent of the real hourly cost of employing that person."
        softwareNote={<>Culinaire Specialist stores an hourly rate per staff member and adds your configured on-cost loading automatically. Every roster hour is priced the moment it is scheduled. <Link to="/register" className="gold underline">Start free</Link>.</>}
      />

      <AnswerBlock
        id="how-to-calculate-labour-cost"
        heading="How to calculate labour cost per plate"
        answer="Break the plate into its prep minutes and service minutes, assign each block to a station with an hourly rate, convert to a per-minute rate, then add on-cost loading. A plate touched by a chef de partie for eight prep minutes plus two service minutes at forty dollars per hour fully loaded costs six dollars and sixty seven cents in labour."
      >
        <DataTable
          caption="Fully loaded hourly rate breakdown"
          columns={["Component", "Typical share", "Notes"]}
          rows={[
            ["Base hourly wage", "100 percent baseline", "The number on the pay slip"],
            ["Superannuation", "10 to 12 percent on top", "Statutory in Australia"],
            ["Payroll tax", "3 to 6 percent on top", "Threshold varies by state"],
            ["Workers compensation", "1 to 3 percent on top", "Higher for kitchen roles"],
            ["Leave loading", "2 to 4 percent on top", "Annual leave, sick leave"],
            ["Total loading", "16 to 25 percent on top", "Apply to every rostered hour"],
          ]}
          testId="labour-cost-table-loading"
        />
      </AnswerBlock>

      <AnswerBlock
        id="labour-targets"
        heading="What are the labour cost benchmarks by service style?"
        answer="Fine dining runs the highest labour cost because technique and station density demand more hands. Quick service sits at the other end. The ranges below are widely accepted operator benchmarks and are useful as a rota audit trigger rather than a hard rule."
      >
        <DataTable
          caption="Labour cost percent by service style"
          columns={["Service style", "Healthy labour cost", "Sales per labour hour"]}
          rows={[
            ["Fine dining", "32 to 38 percent", "AUD 110 to 150"],
            ["Casual full service", "28 to 34 percent", "AUD 150 to 220"],
            ["Cafe", "25 to 32 percent", "AUD 130 to 180"],
            ["Quick service", "22 to 28 percent", "AUD 220 to 320"],
            ["Bar and cocktail", "18 to 26 percent", "AUD 220 to 380"],
            ["Bakery production", "24 to 30 percent", "AUD 120 to 180"],
          ]}
          testId="labour-cost-table-targets"
        />
      </AnswerBlock>

      <AnswerBlock
        id="labour-formulas"
        heading="The five formulas that keep a rota profitable"
        answer="Labour cost percent equals total labour divided by net revenue, times one hundred. Sales per labour hour equals net revenue divided by rostered hours. Labour per plate equals station rate times minutes divided by sixty. Cover ratio equals covers divided by rostered floor hours. Overtime rate equals base rate multiplied by penalty."
      >
        <DataTable
          caption="Core labour cost formulas"
          columns={["Metric", "Formula", "Worked example"]}
          rows={[
            ["Labour cost %", "(Labour cost ÷ Net revenue) × 100", "(4,200 ÷ 13,500) × 100 = 31.1 %"],
            ["Sales per labour hour", "Net revenue ÷ Rostered hours", "13,500 ÷ 128 = 105.5"],
            ["Labour per plate", "(Rate × minutes) ÷ 60", "(40 × 10) ÷ 60 = 6.67"],
            ["Cover ratio", "Covers ÷ Floor labour hours", "180 ÷ 24 = 7.5"],
            ["Fully loaded rate", "Base rate × (1 + loading)", "34 × 1.20 = 40.80"],
            ["Overtime hour cost", "Base rate × penalty × hours", "34 × 1.5 × 2 = 102.00"],
          ]}
          testId="labour-cost-table-formulas"
        />
      </AnswerBlock>

      <AnswerBlock
        id="rota-controls"
        heading="How to keep labour under control during a busy week"
        answer="Publish the rota three days in advance so nobody clocks in on a shift they did not know about. Cap prep hours to a fixed budget on the pass whiteboard. Track sales per labour hour every day, not weekly, so the correction happens inside the same seven days as the mistake."
        softwareNote={<>Culinaire Specialist publishes rosters straight to staff via SMS and email, tallies the total labour cost of every published shift, and flags when a station is over its allocated hours before the pay run.</>}
      />
    </ResourceLayout>
  );
}
